// src/components/TvHero.js
// "TVPSS"-themed hero display: the main images sit inside a retro TV screen
// frame and auto-advance like channels. A remote lets the user flick
// prev/next, jump to a channel, or toggle auto-play.
//
// Images follow the pattern main.jpeg, main2.jpeg, main3.jpeg ... in /public.
// We DON'T hardcode the list — we probe each candidate at mount and keep the
// ones that actually load, so dropping a new main4.jpeg "just works".
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, Power } from 'lucide-react';

const EXTS = ['jpeg', 'jpg', 'png', 'webp'];
const MAX_PROBE = 12; // main .. main12
const AUTO_MS = 4000;

// Filename stems: main, main2, main3 ... mainN (each tried across all EXTS).
const STEMS = Array.from({ length: MAX_PROBE }, (_, i) => (i === 0 ? 'main' : `main${i + 1}`));

const TvHero = () => {
  const [available, setAvailable] = useState([]); // srcs that loaded OK
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [on, setOn] = useState(true); // TV power (visual gimmick)
  const timer = useRef(null);

  // Probe each stem across every supported extension; keep the first that loads.
  useEffect(() => {
    let cancelled = false;
    const probeStem = (stem) =>
      new Promise((resolve) => {
        let idx = 0;
        const tryNext = () => {
          if (idx >= EXTS.length) return resolve(null);
          const src = `/${stem}.${EXTS[idx++]}`;
          const img = new Image();
          img.onload = () => resolve(src);
          img.onerror = tryNext;
          img.src = src;
        };
        tryNext();
      });

    Promise.all(STEMS.map(probeStem)).then((results) => {
      if (cancelled) return;
      const found = results.filter(Boolean);
      setAvailable(found.length ? found : ['/main.jpeg']); // fallback
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const count = available.length;

  const go = (dir) =>
    setIndex((i) => (count ? (i + dir + count) % count : 0));
  const jump = (i) => setIndex(i);

  // Auto-advance channels.
  useEffect(() => {
    if (!playing || !on || count <= 1) return;
    timer.current = setInterval(() => setIndex((i) => (i + 1) % count), AUTO_MS);
    return () => clearInterval(timer.current);
  }, [playing, on, count]);

  const channelLabel = useMemo(
    () => `CH ${String(index + 1).padStart(2, '0')}`,
    [index]
  );

  return (
    <div className="relative z-20 w-full">
      {/* floating slice accent */}
      <div className="pointer-events-none absolute -top-7 -left-6 z-20">
        <svg viewBox="0 0 100 100" className="w-16 md:w-20 rotate-12" aria-hidden="true" fill="none">
          <circle cx="50" cy="50" r="46" fill="#F29200" opacity="0.14" />
          <circle cx="50" cy="50" r="46" stroke="#F29200" strokeWidth="3" />
          <g stroke="#F29200" strokeWidth="1.8" opacity="0.9">
            <line x1="50" y1="14" x2="50" y2="86" />
            <line x1="14" y1="50" x2="86" y2="50" />
            <line x1="24.6" y1="24.6" x2="75.4" y2="75.4" />
            <line x1="75.4" y1="24.6" x2="24.6" y2="75.4" />
          </g>
          <circle cx="50" cy="50" r="3" fill="#F29200" />
        </svg>
      </div>

      {/* ===== TV set ===== */}
      <div className="rounded-[2rem] border-[3px] border-[var(--z-ink)] bg-[var(--z-ink)] p-3 md:p-4 shadow-[10px_12px_0_0_var(--z-accent)]">
        {/* Screen */}
        <div className="relative rounded-2xl overflow-hidden border-2 border-[var(--z-ink)] bg-black aspect-[4/3]">
          {/* scanlines + vignette overlay */}
          <div className="pointer-events-none absolute inset-0 z-10 opacity-[0.12] [background-image:repeating-linear-gradient(0deg,#000_0px,#000_1px,transparent_1px,transparent_3px)]" />
          <div className="pointer-events-none absolute inset-0 z-10 shadow-[inset_0_0_80px_rgba(0,0,0,0.55)] rounded-2xl" />

          {/* Channel badge */}
          <div className="absolute top-3 right-3 z-20 rounded-md bg-black/60 px-2 py-1 font-mono text-xs font-bold tracking-widest text-[#8dff8d]">
            {on ? channelLabel : '— —'}
          </div>
          {/* TVPSS label */}
          <div className="absolute top-3 left-3 z-20 rounded-md bg-[var(--z-accent)] px-2 py-1 font-mono text-[10px] font-bold tracking-widest text-white">
            TVPSS
          </div>

          {/* Image / static */}
          {on ? (
            <AnimatePresence mode="wait">
              <motion.img
                key={available[index] || 'blank'}
                src={available[index]}
                alt={`Paparan ${index + 1}`}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
          ) : (
            <div className="absolute inset-0 grid place-items-center bg-[#111]">
              <span className="font-mono text-sm text-white/40">NO SIGNAL</span>
            </div>
          )}
        </div>

        {/* Dot indicators (channel strip) */}
        <div className="mt-3 flex items-center justify-center gap-2">
          {available.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => jump(i)}
              aria-label={`Tukar ke saluran ${i + 1}`}
              className={`h-2.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-[var(--z-accent)]' : 'w-2.5 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* ===== Remote control ===== */}
      <div className="mt-4 flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => setOn((v) => !v)}
          aria-label="Kuasa TV"
          title="Kuasa"
          className={`grid h-11 w-11 place-items-center rounded-full border-2 border-[var(--z-ink)] shadow-[0_4px_0_0_var(--z-ink)] active:translate-y-0.5 transition ${
            on ? 'bg-[var(--z-accent)] text-white' : 'bg-white text-[var(--z-ink)]'
          }`}
        >
          <Power size={18} />
        </button>

        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Saluran sebelum"
          className="grid h-11 w-11 place-items-center rounded-full border-2 border-[var(--z-ink)] bg-white text-[var(--z-ink)] shadow-[0_4px_0_0_var(--z-ink)] active:translate-y-0.5 transition hover:bg-[var(--z-accent-50)]"
        >
          <ChevronLeft size={20} />
        </button>

        <button
          type="button"
          onClick={() => setPlaying((v) => !v)}
          aria-label={playing ? 'Jeda auto' : 'Main auto'}
          className="inline-flex h-11 items-center gap-2 rounded-full border-2 border-[var(--z-ink)] bg-[var(--z-ink)] px-4 font-bold text-white shadow-[0_4px_0_0_var(--z-accent)] active:translate-y-0.5 transition"
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
          <span className="text-sm">{playing ? 'Auto' : 'Main'}</span>
        </button>

        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Saluran seterusnya"
          className="grid h-11 w-11 place-items-center rounded-full border-2 border-[var(--z-ink)] bg-white text-[var(--z-ink)] shadow-[0_4px_0_0_var(--z-ink)] active:translate-y-0.5 transition hover:bg-[var(--z-accent-50)]"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default TvHero;
